import { useToastStore } from '@/stores/toast.ts'
import { objectOmit } from '@vueuse/core'

export function getApiUrl(protocol: 'http' | 'ws') {
  const host = getHost()
  const secure = window.location.protocol === 'https:'
  if (protocol === 'http') {
    return `${secure ? 'https' : 'http'}://${host}/api`
  }
  if (protocol === 'ws') {
    return `${secure ? 'wss' : 'ws'}://${host}/api`
  }
  return `${protocol}://${host}/api`
}

// noinspection SpellCheckingInspection
const localhosts = ['localhost', '127.0.0.1', '0.0.0.0', '[::1]']

export function getHost() {
  console.log('hosts:', { env: import.meta.env })
  if (import.meta.env.VITE_API_HOST && !localhosts.includes(import.meta.env.VITE_API_HOST)) {
    if (!import.meta.env.VITE_API_PORT) {
      return import.meta.env.VITE_API_HOST
    }
    return `${import.meta.env.VITE_API_HOST}:${import.meta.env.VITE_API_PORT}`
  }
  return window.location.host
}

// Called on a 401 for an authenticated request; resolves to true if a new token is stored and the request can be retried.
// The auth store registers it, since api.ts must not import the store.
let unauthorizedHandler: (() => Promise<boolean>) | null = null
export function setUnauthorizedHandler(handler: (() => Promise<boolean>) | null) {
  unauthorizedHandler = handler
}

export interface PydanticDetail {
  type: string
  loc: (string | number)[]
  msg: string
  ctx?: Record<string, unknown>
  input?: unknown
}
export interface PydanticError {
  detail: PydanticDetail[]
}
export interface OwnError {
  detail: string
}
export type JsonError = PydanticError | OwnError
export interface TypedGoodResponse<R> extends Response {
  json(): Promise<R>
  readonly ok: true
}
export interface TypedErrorResponse extends Response {
  json(): Promise<JsonError>
  readonly ok: false
}
export type TypedResponse<R> = TypedGoodResponse<R> | TypedErrorResponse

export type JsonValue = string | number | boolean | null | JsonObject | JsonArray
export interface JsonObject {
  [key: string]: JsonValue
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface JsonArray extends Array<JsonValue> {}

export type QueryValues = Record<string, string | number | boolean>

export type ExtendedRequestInit<
  Q extends QueryValues = QueryValues,
  J extends JsonValue = JsonValue,
> = RequestInit & {
  query?: Q
  json?: J
}

export function useApiFetch<
  R,
  Q extends QueryValues = QueryValues,
  J extends JsonValue = JsonValue,
>(
  assertName?: string,
  assertOk?: true,
): (path: string, init?: ExtendedRequestInit<Q, J>) => Promise<R>
export function useApiFetch<
  R,
  Q extends QueryValues = QueryValues,
  J extends JsonValue = JsonValue,
>(
  assertName: string,
  assertOk: false,
): (path: string, init?: ExtendedRequestInit<Q, J>) => Promise<TypedResponse<R>>
export function useApiFetch<
  R,
  Q extends QueryValues = QueryValues,
  J extends JsonValue = JsonValue,
>(
  assertName: string = 'API request',
  assertOk: boolean = true,
): (path: string, init?: ExtendedRequestInit<Q, J>) => Promise<R | TypedResponse<R>> {
  const toasts = useToastStore()
  return async (path: string, init?: ExtendedRequestInit<Q, J>) =>
    await apiFetch<R, Q, J>(path, init, assertOk, assertName, toasts)
}

export async function apiFetch<
  R,
  Q extends QueryValues = QueryValues,
  J extends JsonValue = JsonValue,
>(
  path: string,
  init: ExtendedRequestInit<Q, J> | undefined,
  assertOk: boolean,
  assertName?: string,
  toasts?: ReturnType<typeof useToastStore>,
): Promise<R | TypedResponse<R>>
export async function apiFetch<
  R,
  Q extends QueryValues = QueryValues,
  J extends JsonValue = JsonValue,
>(
  path: string,
  init: ExtendedRequestInit<Q, J> | undefined,
  assertOk: true,
  assertName?: string,
  toasts?: ReturnType<typeof useToastStore>,
): Promise<R>
export async function apiFetch<
  R,
  Q extends QueryValues = QueryValues,
  J extends JsonValue = JsonValue,
>(path: string, init?: ExtendedRequestInit<Q, J>): Promise<TypedResponse<R>>
export function apiFetch<R, Q extends QueryValues = QueryValues, J extends JsonValue = JsonValue>(
  path: string,
  init: ExtendedRequestInit<Q, J> | undefined,
  assertOk: false,
  assertName?: string,
  toasts?: ReturnType<typeof useToastStore>,
): Promise<TypedResponse<R>>
export async function apiFetch<
  R,
  Q extends QueryValues = QueryValues,
  J extends JsonValue = JsonValue,
>(
  path: string,
  init?: ExtendedRequestInit<Q, J>,
  assertOk: boolean = false,
  assertName: string = 'API request',
  toasts: ReturnType<typeof useToastStore> | undefined = undefined,
): Promise<R | TypedResponse<R>> {
  const headers = new Headers(init?.headers || {})
  if (!headers.has('Content-Type') && init?.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  const url = new URL(`${getApiUrl('http')}${path}`)
  if (init?.query) {
    const params =
      init.query instanceof URLSearchParams
        ? init.query
        : new URLSearchParams(Object.entries(init.query).map(([k, v]) => [k, String(v)]))
    url.search = params.toString()
    // remove query from init to avoid issues
    init = objectOmit(init, ['query'])
  }
  if (init?.json) {
    init = {
      ...init,
      body: JSON.stringify(init.json, undefined, 2),
    }
    init = objectOmit(init, ['json'])
    headers.set('Content-Type', 'application/json')
  }
  init = init || {}
  const send = () => {
    const sentHeaders = new Headers(headers)
    const token = localStorage.getItem('auth_token')
    if (token) sentHeaders.set('Authorization', `Bearer ${token}`)
    return fetch(url, { ...init, headers: sentHeaders })
  }
  let resp = await send()
  // An expired token: renew it and send the request again, once. Not for the auth routes, they are what renews it.
  if (resp.status === 401 && unauthorizedHandler && !path.startsWith('/auth/')) {
    if (localStorage.getItem('auth_token') && (await unauthorizedHandler())) {
      resp = await send()
    }
  }
  if (assertOk) {
    const txt = await resp.text()
    let details: string | null = null
    if (!resp.ok) {
      try {
        const error = JSON.parse(txt) as JsonError
        if (Array.isArray(error.detail)) {
          details = error.detail
            .map((detail: PydanticDetail): string => {
              let msg = 'Validation Error ('
              if (detail.msg) {
                msg += `${detail.msg} `
              }
              if (detail.type) {
                msg += `of type ${detail.type} `
              }
              /*
            if (detail.ctx) {
              msg += `(${Object.entries(detail.ctx).map(([k, v]) => `${k}=${v}`).join(', ')}) `;
            }
            */
              if (detail.loc) {
                msg += `at ${detail.loc.join('.')} `
              }
              if (detail.input) {
                msg += `for input ${JSON.stringify(detail.input, null, 2)} `
              }
              msg = msg.trimEnd()
              msg += ')'
              if (msg === 'Validation Error ()') {
                msg = 'Validation Error'
              }
              return msg
            })
            .join(',\n')
        } else {
          details = error.detail ? `:\n${error.detail}` : ''
        }
      } catch (e) {
        details = `Json parsing failed: ${e}\n${txt.slice(0, 50)}${txt.length > 50 ? '…' : ''}`
      }
    } else {
      try {
        return JSON.parse(txt) as R
      } catch (e) {
        details = `Json parsing failed: ${e}\n${txt.slice(0, 50)}${txt.length > 50 ? '…' : ''}`
      }
    }
    if (toasts === undefined) {
      toasts = useToastStore()
    }
    const description = `Status: ${resp.status} ${details}`.trimEnd()
    toasts.add(`Failed to load ${assertName}`, 'error', description)
    throw new Error(
      `Loading ${assertName} failed (${resp.status}):\nDetails: ${details}\nBody:\n${txt}`,
    )
  }
  return resp
}
